import { showAppUpdate } from "./updateStore";

const UPDATE_CHECK_INTERVAL_MS = 5 * 60_000;
const UPDATE_CHECK_THROTTLE_MS = 30_000;

type ServiceWorkerUpdateDependencies = {
  serviceWorker: ServiceWorkerContainer;
  browserWindow: Window;
  onUpdateAvailable?: (update: () => Promise<void>) => void;
  now?: () => number;
  logger?: Pick<Console, "log" | "warn" | "error">;
};

export async function checkForServiceWorkerUpdate({
  serviceWorker,
  browserWindow,
  onUpdateAvailable = showAppUpdate,
  now = Date.now,
  logger = console,
}: ServiceWorkerUpdateDependencies): Promise<void> {
  let hadController = Boolean(serviceWorker.controller);
  let finishActivation: (() => void) | undefined;
  const reload = async () => { browserWindow.location.reload(); };
  const handleControllerChange = () => {
    if (finishActivation) {
      finishActivation();
    } else if (hadController) {
      // Another tab may activate the update. This tab still waits for its user's click.
      onUpdateAvailable(reload);
    }
    hadController = true;
  };
  serviceWorker.addEventListener("controllerchange", handleControllerChange);

  try {
    const registration = await serviceWorker.register("/service-worker.js", {
      scope: "/",
      updateViaCache: "none",
    });

    const offerWaitingUpdate = () => {
      if (!registration.waiting || !serviceWorker.controller) return;
      onUpdateAvailable(async () => {
        const worker = registration.waiting;
        if (worker) {
          await new Promise<void>((resolve, reject) => {
            const timeout = browserWindow.setTimeout(() => {
              finishActivation = undefined;
              reject(new Error("Update activation timed out"));
            }, 15_000);
            finishActivation = () => {
              browserWindow.clearTimeout(timeout);
              finishActivation = undefined;
              resolve();
            };
            try {
              worker.postMessage({ type: "SKIP_WAITING" });
            } catch (error) {
              browserWindow.clearTimeout(timeout);
              finishActivation = undefined;
              reject(error);
            }
          });
        }
        await reload();
      });
    };
    const watchInstallingWorker = () => {
      const worker = registration.installing;
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed") offerWaitingUpdate();
      });
    };
    registration.addEventListener("updatefound", watchInstallingWorker);
    watchInstallingWorker();
    offerWaitingUpdate();

    let checking = false;
    let lastCheck = -Infinity;
    const check = async () => {
      if (browserWindow.document.visibilityState === "hidden" ||
          !browserWindow.navigator.onLine || checking || now() - lastCheck < UPDATE_CHECK_THROTTLE_MS) return;
      checking = true;
      lastCheck = now();
      try {
        await registration.update();
        offerWaitingUpdate();
      } catch (error) {
        logger.warn("PWA update check failed", error);
      } finally {
        checking = false;
      }
    };
    browserWindow.setInterval(() => { void check(); }, UPDATE_CHECK_INTERVAL_MS);
    browserWindow.addEventListener("focus", check);
    browserWindow.addEventListener("online", check);
    browserWindow.document.addEventListener("visibilitychange", check);
    await check();
  } catch (error) {
    serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    logger.error("PWA service worker registration failed", error);
  }
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  void checkForServiceWorkerUpdate({ serviceWorker: navigator.serviceWorker, browserWindow: window });
}
